import { useTranslation } from "react-i18next";
import { cn, Input, Label } from "@nexus/ui";
import {
  checkCronSchedule,
  SCHEDULED_JOB_CRON_TEMPLATES,
  type CronFieldName,
  type CronScheduleCheck,
  type CronScheduleIssue,
  type CronSummaryKey,
} from "../lib/scheduled-job-cron";

interface ScheduledJobCronFieldProps {
  id: string;
  value: string;
  disabled?: boolean;
  onChange: (schedule: string) => void;
  label?: string;
}

/**
 * Cron input with selectable presets, live validation, and a plain-English
 * readback. Presets are bordered chips (not ghost grey text) so they read as
 * actions; the summary sits in a tinted band so it is not another muted line.
 */
export function ScheduledJobCronField({
  id,
  value,
  disabled = false,
  onChange,
  label,
}: ScheduledJobCronFieldProps) {
  const { t } = useTranslation();
  const check = checkCronSchedule(value);
  const statusId = `${id}-status`;
  const presetsId = `${id}-presets`;
  const fieldLabel = label ?? t("admin.scheduledJobs.cron.label", "Cron schedule");
  const normalizedValue = value.trim().replace(/\s+/g, " ");

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>{fieldLabel}</Label>
        <Input
          id={id}
          value={value}
          disabled={disabled}
          spellCheck={false}
          aria-invalid={!check.ok}
          aria-describedby={`${presetsId} ${statusId}`}
          placeholder="0 * * * *"
          onChange={(event) => onChange(event.target.value)}
          className="h-9 font-mono text-xs tracking-wide"
        />
      </div>

      <div className="flex flex-col gap-2" id={presetsId}>
        <p className="text-[11px] font-medium text-foreground">
          {t("admin.scheduledJobs.cron.presets", "Presets")}
        </p>
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label={t("admin.scheduledJobs.cron.presets", "Presets")}
        >
          {SCHEDULED_JOB_CRON_TEMPLATES.map((template) => {
            const isActive = normalizedValue === template.expression;
            return (
              <button
                key={template.id}
                type="button"
                disabled={disabled}
                aria-pressed={isActive}
                onClick={() => onChange(template.expression)}
                className={cn(
                  "rounded-sm border px-2.5 py-1.5 text-xs transition-colors duration-150",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                  "disabled:pointer-events-none disabled:opacity-40",
                  isActive
                    ? "border-primary bg-primary/5 font-medium text-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted",
                )}
              >
                {t(`admin.scheduledJobs.cron.templates.${template.id}`, template.label)}
              </button>
            );
          })}
        </div>
      </div>

      <p
        id={statusId}
        aria-live="polite"
        className={cn(
          "rounded-md px-3 py-2 text-xs leading-5",
          check.ok
            ? "bg-muted text-foreground"
            : "bg-error-subtle text-error-subtle-foreground",
        )}
      >
        {check.ok ? cronSummaryText(check, t) : cronIssueText(check.issue, t)}
      </p>
    </div>
  );
}

export function isValidCronSchedule(value: string): boolean {
  return checkCronSchedule(value).ok;
}

function cronSummaryText(
  check: Extract<CronScheduleCheck, { ok: true }>,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string {
  const params = check.summaryParams ?? {};
  return cronSummaryByKey(check.summaryKey, t, params);
}

function cronSummaryByKey(
  key: CronSummaryKey,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  params: Record<string, string>,
): string {
  switch (key) {
    case "everyMinute":
      return t("admin.scheduledJobs.cron.summary.everyMinute", "Runs every minute.");
    case "everyNMinutes":
      return t("admin.scheduledJobs.cron.summary.everyNMinutes", "Runs every {{n}} minutes.", {
        n: params.n,
      });
    case "hourly":
      return t(
        "admin.scheduledJobs.cron.summary.hourly",
        "Runs hourly at minute {{minute}}.",
        { minute: params.minute },
      );
    case "everyNHours":
      return t(
        "admin.scheduledJobs.cron.summary.everyNHours",
        "Runs every {{n}} hours at minute {{minute}}.",
        { n: params.n, minute: params.minute },
      );
    case "dailyAt":
      return t(
        "admin.scheduledJobs.cron.summary.dailyAt",
        "Runs daily at {{time}} in the selected timezone.",
        { time: params.time },
      );
    case "weekdaysAt":
      return t(
        "admin.scheduledJobs.cron.summary.weekdaysAt",
        "Runs weekdays (Mon–Fri) at {{time}}.",
        { time: params.time },
      );
    case "weeklyAt":
      return t(
        "admin.scheduledJobs.cron.summary.weeklyAt",
        "Runs weekly on day {{day}} at {{time}} (0 = Sunday).",
        { day: params.day, time: params.time },
      );
    case "generic":
      return t(
        "admin.scheduledJobs.cron.summary.generic",
        "Valid Cloud Scheduler cron: {{expression}}.",
        { expression: params.expression },
      );
  }
}

function cronIssueText(
  issue: CronScheduleIssue,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string {
  switch (issue.kind) {
    case "empty":
      return t("admin.scheduledJobs.cron.error.empty", "Enter a five-field cron expression.");
    case "field-count":
      return t(
        "admin.scheduledJobs.cron.error.fieldCount",
        "Need five fields (minute hour day-of-month month day-of-week) — found {{count}}.",
        { count: issue.count },
      );
    case "field":
      return issue.reason === "range"
        ? t(
            "admin.scheduledJobs.cron.error.fieldRange",
            "Invalid {{field}} — value is out of range.",
            { field: fieldLabel(issue.field, t) },
          )
        : t(
            "admin.scheduledJobs.cron.error.fieldSyntax",
            "Invalid {{field}} — use *, numbers, ranges, lists, or */n steps.",
            { field: fieldLabel(issue.field, t) },
          );
  }
}

function fieldLabel(field: CronFieldName, t: (key: string, fallback: string) => string): string {
  switch (field) {
    case "minute":
      return t("admin.scheduledJobs.cron.fields.minute", "minute");
    case "hour":
      return t("admin.scheduledJobs.cron.fields.hour", "hour");
    case "dayOfMonth":
      return t("admin.scheduledJobs.cron.fields.dayOfMonth", "day of month");
    case "month":
      return t("admin.scheduledJobs.cron.fields.month", "month");
    case "dayOfWeek":
      return t("admin.scheduledJobs.cron.fields.dayOfWeek", "day of week");
  }
}
